import type {
  JobCreateRequest,
  Product,
  Term,
  Stage,
} from "@acs/shared";

const BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/$/, "");

export const TOKEN_KEY = "acs_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function http<T>(path: string, init?: RequestInit & { skipContentType?: boolean }): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (!init?.skipContentType) headers["Content-Type"] = "application/json";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const { skipContentType: _, ...fetchInit } = (init ?? {}) as any;
  const res = await fetch(`${BASE}${path}`, { headers, ...fetchInit });

  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Oturum sona erdi.");
  }
  if (!res.ok) {
    const text = await res.text();
    let friendlyMsg = "Bir hata oluştu.";
    try {
      const body = JSON.parse(text);
      if (body.error) {
        const e = body.error as string;
        if (e.includes("Gemini") || e.includes("fal") || e.includes("Arcads") || e.includes("{\n") || e.length > 120) {
          friendlyMsg = res.status === 502 ? "Yapay zeka servisi şu an yanıt vermiyor, lütfen tekrar deneyin." : "Bir hata oluştu, lütfen tekrar deneyin.";
        } else {
          friendlyMsg = e;
        }
      }
    } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent("acs-api-error", { detail: friendlyMsg }));
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    http<{ ok: boolean; token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  verifyToken: () => http<{ ok: boolean }>("/auth/verify"),

  health: () => http<{ ok: boolean; env: string }>("/health"),

  // Products
  listProducts: () => http<{ ok: boolean; products: Product[] }>("/products"),
  getProduct: (id: string) =>
    http<{ ok: boolean; product: Product & { types: string[] } }>(
      `/products/${id}`,
    ),
  createProduct: (body: {
    name: string;
    tag: string;
    drive_folder_id: string;
    types?: string[];
  }) =>
    http<{ ok: boolean; id: string }>("/products", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateProduct: (
    id: string,
    body: { name?: string; drive_folder_id?: string; types?: string[]; angles?: string[]; audiences?: string[]; brand_guidelines?: string; onboarding_json?: string; subtitle_vocabulary?: string; subtitle_preset?: string; subtitle_highlight_color?: string; subtitle_position?: string; hook_drive_folder_id?: string; body_drive_folder_id?: string },
  ) =>
    http<{ ok: boolean }>(`/products/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  onboardProduct: (id: string, answers: Record<string, string>) =>
    http<{ ok: boolean; brand_guidelines: string }>(`/products/${id}/onboard`, {
      method: "POST",
      body: JSON.stringify(answers),
    }),
  deleteProduct: (id: string) =>
    http<{ ok: boolean }>(`/products/${id}`, { method: "DELETE" }),

  // Features
  listFeatures: (productId: string) =>
    http<{ ok: boolean; features: { id: string; name: string; pain_points: string[]; audiences: string[] }[] }>(
      `/products/${productId}/features`,
    ),
  addFeature: (productId: string, name: string) =>
    http<{ ok: boolean; id: string }>(`/products/${productId}/features`, {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  deleteFeature: (productId: string, featureId: string) =>
    http<{ ok: boolean }>(`/products/${productId}/features/${featureId}`, { method: "DELETE" }),
  updateFeature: (productId: string, featureId: string, body: { name?: string; pain_points?: string[]; audiences?: string[] }) =>
    http<{ ok: boolean }>(`/products/${productId}/features/${featureId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // Custom types
  listCustomTypes: (productId: string, kind?: "image" | "video") =>
    http<{ ok: boolean; custom_types: any[] }>(
      `/products/${productId}/custom-types${kind ? `?kind=${kind}` : ""}`,
    ),
  createCustomType: (productId: string, formData: FormData) =>
    http<{ ok: boolean; custom_type: any }>(`/products/${productId}/custom-types`, {
      method: "POST",
      body: formData,
      // Don't set Content-Type — browser sets it with boundary for multipart
      skipContentType: true,
    }),
  deleteCustomType: (productId: string, typeId: string) =>
    http<{ ok: boolean }>(`/products/${productId}/custom-types/${typeId}`, { method: "DELETE" }),
  getProductCosts: (id: string) =>
    http<{
      ok: boolean;
      grand_total_usd: number;
      pillars: Record<string, { count: number; unit: string; cost_usd: number }>;
      lang_breakdown: Record<string, number>;
    }>(`/products/${id}/costs`),

  // Terms
  listTerms: (productId: string) =>
    http<{ ok: boolean; terms: Term[] }>(`/terms?product_id=${productId}`),
  createTerm: (productId: string, name: string) =>
    http<{ ok: boolean; id: string }>("/terms", {
      method: "POST",
      body: JSON.stringify({ product_id: productId, name }),
    }),
  deleteTerm: (id: string) =>
    http<{ ok: boolean }>(`/terms/${id}`, { method: "DELETE" }),

  // Stages
  listStages: () => http<{ ok: boolean; stages: Stage[] }>("/stages"),

  // Jobs
  createJob: (body: JobCreateRequest) =>
    http<{ ok: boolean; id: string }>("/jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listJobs: (productId?: string) =>
    http<{ ok: boolean; jobs: any[] }>(
      `/jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  getJob: (id: string) => http<{ ok: boolean; job: any; media: any[]; files: any[]; costs: any[] }>(`/jobs/${id}`),
  cancelJob: (id: string) =>
    http<{ ok: boolean }>(`/jobs/${id}/cancel`, { method: "POST" }),
  requeueJob: (id: string) =>
    http<{ ok: boolean }>(`/jobs/${id}/requeue`, { method: "POST" }),
  createEditJob: (body: {
    product_id: string;
    codes: string[];
    operation: "translate" | "resize" | "video" | "image-edit" | "version" | "recreate" | "hook_body_compose";
    languages?: string[];
    sizes?: string[];
    video_duration?: number;
    video_size?: string;
    // image-edit
    size?: string;
    language?: string;
    prompt?: string;
    // version
    vary_hook?: boolean;
    vary_body?: boolean;
    vary_cta?: boolean;
    version_count?: number;
  }) =>
    http<{ ok: boolean; id: string }>("/edit-jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Edit jobs
  listEditJobs: (productId?: string) =>
    http<{ ok: boolean; edit_jobs: any[] }>(
      `/edit-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  cancelEditJob: (id: string) =>
    http<{ ok: boolean }>(`/edit-jobs/${id}/cancel`, { method: "POST" }),

  // UGC Jobs
  createUGCJob: (body: {
    product_id: string;
    term_id?: string;
    creative_type: "Panel" | "Influencer POV" | "Podcast" | "Doctor" | "Custom" | "Twitter";
    platform?: "app" | "web";
    audience: string;
    pain_point: string;
    delivery_type: string;
    age: string;
    gender: string;
    nation: string;
    outfit?: string;
    scenario_id?: string;
    also_crop_square?: boolean;
  }) =>
    http<{ ok: boolean; id: string }>("/ugc-jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listUGCJobs: (productId?: string) =>
    http<{ ok: boolean; ugc_jobs: any[] }>(
      `/ugc-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  cancelUGCJob: (id: string) =>
    http<{ ok: boolean }>(`/ugc-jobs/${id}/cancel`, { method: "POST" }),
  retryUGCJob: (id: string) =>
    http<{ ok: boolean }>(`/ugc-jobs/${id}/retry`, { method: "POST" }),
  listUGCScenarios: (productId: string) =>
    http<{ ok: boolean; ugc_scenarios: {
      id: string; name: string; description: string; avatar_description?: string;
      location?: string | null; environment?: string | null; music?: string | null;
      cam_angle?: string | null; cam_movement?: string | null; emotion?: string | null;
      role_archetype?: string | null; season?: string | null;
    }[] }>(`/products/${productId}/ugc-scenarios`),
  createUGCScenarioFromVideo: (productId: string, file: File) => {
    const fd = new FormData();
    fd.append("video", file);
    return http<{ ok: boolean; ugc_scenario: { id: string; name: string; description: string; avatar_description?: string; multi_shot: boolean } }>(
      `/products/${productId}/ugc-scenarios/from-video`,
      { method: "POST", body: fd, skipContentType: true }
    );
  },
  createUGCScenario: (productId: string, body: { name: string; description: string; avatar_description?: string }) =>
    http<{ ok: boolean; ugc_scenario: { id: string; name: string; description: string; avatar_description?: string } }>(`/products/${productId}/ugc-scenarios`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteUGCScenario: (productId: string, scenarioId: string) =>
    http<{ ok: boolean }>(`/products/${productId}/ugc-scenarios/${scenarioId}`, { method: "DELETE" }),

  getTypeSystemPrompt: (productId: string, type: string, provider?: string) =>
    http<{ ok: boolean; system_prompt: string }>(`/products/${productId}/type-system-prompt/${encodeURIComponent(type)}${provider ? `?provider=${provider}` : ""}`),

  // Avatar Jobs
  getActorUsage: () =>
    http<{ ok: boolean; counts: Record<string, number> }>(`/avatar-jobs/actor-usage`),
  listAvatarSituations: (params: { page?: number; limit?: number; gender?: string; age?: string; tag?: string }) =>
    http<{ count: number; items: any[] }>(
      `/avatar-jobs/situations?${new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)]),
      ).toString()}`,
    ),
  createAvatarJob: (body: {
    product_id: string;
    term_id?: string;
    situation_id: string;
    voice_id: string;
    text: string;
    actor_name: string;
    situation_image_url: string;
    image_code?: string;
  }) =>
    http<{ ok: boolean; id: string }>("/avatar-jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listAvatarJobs: (productId?: string) =>
    http<{ ok: boolean; avatar_jobs: any[] }>(
      `/avatar-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  listLongStoryJobs: (productId?: string) =>
    http<{ ok: boolean; long_story_jobs: any[] }>(
      `/long-story-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  listVoxAdJobs: (productId?: string) =>
    http<{ ok: boolean; vox_ad_jobs: any[] }>(
      `/vox-ad-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  listBrollAdJobs: (productId?: string) =>
    http<{ ok: boolean; broll_ad_jobs: any[] }>(
      `/broll-ad-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  cancelLongStoryJob: (id: string) =>
    http<{ ok: boolean }>(`/long-story-jobs/${id}/cancel`, { method: "POST" }),
  cancelVoxAdJob: (id: string) =>
    http<{ ok: boolean }>(`/vox-ad-jobs/${id}/cancel`, { method: "POST" }),
  cancelBrollAdJob: (id: string) =>
    http<{ ok: boolean }>(`/broll-ad-jobs/${id}/cancel`, { method: "POST" }),
  cancelAvatarJob: (id: string) =>
    http<{ ok: boolean }>(`/avatar-jobs/${id}/cancel`, { method: "POST" }),
  generateAvatarScript: (prompt: string) =>
    http<{ ok: boolean; text: string }>("/avatar-jobs/generate-script", {
      method: "POST",
      body: JSON.stringify({ prompt }),
    }),

  // Compose Jobs
  createComposeJob: (body: {
    product_id: string;
    avatar_job_id: string;
    image_code: string;
    image_language?: string;
    image_size?: string;
    format: "split" | "takeover" | "rembg";
  }) =>
    http<{ ok: boolean; id: string }>("/compose-jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getComposePreviews: (productId: string) =>
    http<{ ok: boolean; previews: Record<string, string> }>(`/compose-jobs/format-previews?product_id=${productId}`),
  listComposeJobs: (productId?: string) =>
    http<{ ok: boolean; compose_jobs: any[] }>(
      `/compose-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  cancelComposeJob: (id: string) =>
    http<{ ok: boolean }>(`/compose-jobs/${id}/cancel`, { method: "POST" }),
  retryComposeJob: (id: string) =>
    http<{ ok: boolean }>(`/compose-jobs/${id}/retry`, { method: "POST" }),

  // Media
  listMedia: (productId?: string) =>
    http<{ ok: boolean; media: any[] }>(
      `/media${productId ? `?product_id=${productId}` : ""}`,
    ),

  getTypePreviews: (productId: string) =>
    http<{ ok: boolean; previews: Record<string, string> }>(
      `/media/type-previews?product_id=${productId}`,
    ),

  getTracking: (productId: string) =>
    http<{ ok: boolean; rows: any[] }>(`/media/tracking?product_id=${productId}`),

  updateTracking: (productId: string, code: string, patch: { meta?: boolean; tiktok?: boolean; google?: boolean; country?: string }) =>
    http<{ ok: boolean }>(`/media/tracking/${code}?product_id=${productId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // Campaigns
  createCampaign: (body: any) =>
    http<{ ok: boolean; campaign_id: string }>("/campaigns", { method: "POST", body: JSON.stringify(body) }),
  listCampaigns: (productId: string) =>
    http<{ ok: boolean; campaigns: any[] }>(`/campaigns?product_id=${productId}`),
  cancelCampaign: (id: string) =>
    http<{ ok: boolean }>(`/campaigns/${id}/cancel`, { method: "POST" }),
  deleteCampaign: (id: string) =>
    http<{ ok: boolean }>(`/campaigns/${id}`, { method: "DELETE" }),
  getCampaignCodes: (id: string) =>
    http<{ ok: boolean; image_codes: Array<{ code: string; job_id: string }>; ugc_jobs: Array<{ id: string; creative_type: string; status: string; index: number }> }>(`/campaigns/${id}/codes`),

  // Chat
  chatCreate: (body: { product_id: string; product_name?: string; product_features?: any[]; messages: Array<{ role: string; content: string }> }) =>
    http<{ ok: boolean; message: string; choices?: { message: string; fields: Array<{ key: string; label: string; options: string[] }> }; action?: any; create_field?: { field_type: string; value: string; message: string; features: Array<{ id: string; name: string }> } }>("/chat/create", { method: "POST", body: JSON.stringify(body) }),

  // AI Chat
  aiChat: (body: {
    messages: Array<{ role: "user" | "assistant"; content: string | any[] }>;
    product_id?: string;
    product_name?: string;
  }) =>
    http<{ ok: boolean; message: string; assistant_content?: any[]; tool_results?: any[] }>("/ai-chat", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Storyboard Jobs
  createStoryboardJob: (body: {
    product_id: string;
    feature_id: string;
    audience: string;
    pain_point: string;
    creator_type: string;
  }) =>
    http<{ ok: boolean; id: string }>("/storyboard-jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listStoryboardJobs: (productId?: string) =>
    http<{ ok: boolean; storyboard_jobs: any[] }>(
      `/storyboard-jobs${productId ? `?product_id=${productId}` : ""}`,
    ),
  cancelStoryboardJob: (id: string) =>
    http<{ ok: boolean }>(`/storyboard-jobs/${id}/cancel`, { method: "POST" }),
  retryStoryboardJob: (id: string) =>
    http<{ ok: boolean }>(`/storyboard-jobs/${id}/retry`, { method: "POST" }),

  // Settings
  getSettings: () =>
    http<{ ok: boolean; keys: Record<string, boolean>; drive_editor_email: string }>(
      "/settings",
    ),

  // Canvas / Creative Builder
  saveFlowScenario: (productId: string, name: string, flowConfig: unknown) =>
    http<{ ok: boolean; custom_type: { id: string; name: string } }>(
      `/products/${productId}/custom-types/flow`,
      { method: "POST", body: JSON.stringify({ name, flow_config: flowConfig }) },
    ),
  builderSubmit: (body: {
    product_id?: string;
    kind: "image" | "video";
    model: string;
    prompt: string;
    aspect_ratio?: string;
    duration?: number;
    image_url?: string;
    seedance_mode?: "startImage" | "element";
  }) =>
    http<{ ok: boolean; id?: string; error?: string }>(`/creative-builder/submit`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  builderPoll: (id: string) =>
    http<{ ok: boolean; status: string; result_url?: string; video_bytes?: string; is_bytes?: boolean; error?: string }>(
      `/creative-builder/poll/${id}`,
    ),
  buildPrompt: (idea: string, kind: "image" | "video") =>
    http<{ ok: boolean; prompt?: string; error?: string }>(`/creative-builder/build-prompt`, {
      method: "POST",
      body: JSON.stringify({ idea, kind }),
    }),
  analyzeAsType: (prompt: string, kind: "image" | "video") =>
    http<{
      ok: boolean;
      detected_hook?: string | null;
      detected_body?: string | null;
      detected_cta?: string | null;
      system_prompt?: string;
      error?: string;
    }>(`/creative-builder/analyze-as-type`, {
      method: "POST",
      body: JSON.stringify({ prompt, kind }),
    }),
  saveCustomTypeText: (productId: string, name: string, systemPrompt: string, kind: "image" | "video") =>
    http<{ ok: boolean; custom_type: { id: string; name: string } }>(
      `/products/${productId}/custom-types/text`,
      { method: "POST", body: JSON.stringify({ name, system_prompt: systemPrompt, kind }) },
    ),

  // ── Performance (CSV ingestion + genome + benchmark) ──────────────────────
  updateUGCScenario: (productId: string, scenarioId: string, body: Partial<{
    name: string; description: string; avatar_description: string;
    location: string; environment: string; music: string;
    cam_angle: string; cam_movement: string; emotion: string;
    role_archetype: string; season: string;
  }>) =>
    http<{ ok: boolean; ugc_scenario: Record<string, string | null> }>(
      `/products/${productId}/ugc-scenarios/${scenarioId}`,
      { method: "PUT", body: JSON.stringify(body) },
    ),
  parsePerformanceCsv: (csv: string, platform: string) =>
    http<{
      ok: boolean;
      headers: string[];
      row_count: number;
      preview: Record<string, string>[];
      suggested_mapping: Record<string, string>;
      mapping_from_preset: boolean;
      match_preview: { matched: number; unmatched_sample: string[] };
    }>(`/performance/parse`, { method: "POST", body: JSON.stringify({ csv, platform }) }),
  ingestPerformanceCsv: (body: {
    csv: string;
    filename: string;
    platform: string;
    mapping: Record<string, string>;
    period_start?: string;
    period_end?: string;
    save_preset?: boolean;
  }) =>
    http<{ ok: boolean; upload_id: string; row_count: number; matched_count: number; unmatched: string[] }>(
      `/performance/uploads`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  listPerformanceUploads: () =>
    http<{ ok: boolean; uploads: {
      id: string; filename: string; platform: string;
      period_start: string | null; period_end: string | null;
      row_count: number; matched_count: number; unmatched_json: string | null; created_at: string;
    }[] }>(`/performance/uploads`),
  deletePerformanceUpload: (id: string) =>
    http<{ ok: boolean }>(`/performance/uploads/${id}`, { method: "DELETE" }),
  getPerformanceCreatives: (productId: string) =>
    http<{
      ok: boolean;
      creatives: {
        code: string;
        ad_name: string | null;
        variables: Record<string, string>;
        metrics: Record<string, number | null>;
        evaluation: Record<string, "pass" | "fail" | null>;
        eligible: boolean;
        status: "winner" | "loser" | "insufficient" | "no_data";
        row_count: number;
        first_period: string | null;
        last_period: string | null;
      }[];
      benchmarks: Record<string, { target: number; direction: string; overridden: boolean }>;
      settings: { min_spend: number; min_impressions: number; primary_metric: string };
    }>(`/performance/creatives?product_id=${productId}`),
  getBenchmarks: (productId: string) =>
    http<{
      ok: boolean;
      benchmarks: Record<string, { target: number; direction: string; overridden: boolean }>;
      settings: { min_spend: number; min_impressions: number; primary_metric: string };
      defaults: Record<string, number>;
    }>(`/performance/benchmarks?product_id=${productId}`),
  putBenchmarks: (productId: string, benchmarks: Record<string, number | null>, settings?: Partial<{ min_spend: number; min_impressions: number; primary_metric: string }>) =>
    http<{ ok: boolean }>(`/performance/benchmarks`, {
      method: "PUT",
      body: JSON.stringify({ product_id: productId, benchmarks, settings }),
    }),
  backfillGenome: () =>
    http<{ ok: boolean; media_backfilled: number; ugc_backfilled: number }>(
      `/performance/backfill-genome`,
      { method: "POST" },
    ),
  getPerformanceInsights: (productId: string) =>
    http<{
      ok: boolean;
      insights: {
        variable: string;
        value: string;
        creative_count: number;
        total_spend: number;
        metrics: Record<string, number | null>;
        baseline: Record<string, number | null>;
        lift_pct: Record<string, number | null>;
      }[];
      note?: string | null;
      settings: { min_spend: number; min_impressions: number; primary_metric: string };
    }>(`/performance/insights?product_id=${productId}`),
  getPerformanceInsightsSummary: (productId: string) =>
    http<{ ok: boolean; summary: string }>(`/performance/insights/summary`, {
      method: "POST",
      body: JSON.stringify({ product_id: productId }),
    }),

  // ── Brief Generator (Faz 4) ────────────────────────────────────────────────
  generateNewHooks: (productId: string, featureId: string, count?: number) =>
    http<{ ok: boolean; feature_name: string; suggestions: { pain_point: string; rationale: string }[]; error?: string }>(
      `/briefs/new-hook`,
      { method: "POST", body: JSON.stringify({ product_id: productId, feature_id: featureId, count }) },
    ),
  varyScenario: (productId: string, scenarioId: string, vary: string[], count?: number) =>
    http<{
      ok: boolean;
      source_scenario: { id: string; name: string };
      variations: { name: string; variables: Record<string, string>; description_update_note: string }[];
      error?: string;
    }>(`/briefs/vary-scenario`, {
      method: "POST",
      body: JSON.stringify({ product_id: productId, scenario_id: scenarioId, vary, count }),
    }),
  // ── Chat-bazlı üretim (yeni UI) ─────────────────────────────────────────
  sendChat: (productId: string, productName: string | undefined, messages: { role: "user" | "assistant"; content: string }[]) =>
    http<{ ok: boolean; message: string; created_jobs: { kind: string; id: string }[]; error?: string }>("/ai-chat", {
      method: "POST",
      body: JSON.stringify({ messages, product_id: productId, product_name: productName }),
    }),
  getChatJobStatus: (jobType: string, id: string) =>
    http<{
      ok: boolean;
      status: string;
      step: string | null;
      error: string | null;
      code: string | null;
      qa: { pass: boolean; decision: string; issues: string[] } | null;
      stage_hint: string[];
    }>(`/ai-chat/status?job_type=${jobType}&id=${id}`),

  duplicateWinner: (productId: string, code: string, level: "L1_resize" | "L2_element_swap" | "L3_scene_change") =>
    http<{ ok: boolean; edit_job_id?: string; operation?: string; error?: string }>(`/briefs/duplicate-winner`, {
      method: "POST",
      body: JSON.stringify({ product_id: productId, code, level }),
    }),
  saveScenarioVariation: (productId: string, sourceScenarioId: string, name: string, variables: Record<string, string>) =>
    http<{ ok: boolean; scenario_id: string }>(`/briefs/save-scenario-variation`, {
      method: "POST",
      body: JSON.stringify({ product_id: productId, source_scenario_id: sourceScenarioId, name, variables }),
    }),

  // ── Asset Library (Hook/Body/Packshot havuzları) ───────────────────────────
  getAssetPool: (productId: string, pool: "hook" | "body" | "packshot") =>
    http<{
      ok: boolean;
      groups: { feature: string | null; matchesFeature: boolean; files: { id: string; name: string; mimeType: string }[] }[];
      note?: string;
    }>(`/asset-library?product_id=${productId}&pool=${pool}`),

  // ── Video QA (Faz 5) ────────────────────────────────────────────────────
  getIdeaTree: (productId: string) =>
    http<{
      ok: boolean;
      tree: {
        label: string;
        level: string;
        summary: { winners: number; losers: number; qa_fails: number; total: number };
        children: any[];
        executions: { code: string; angle: string | null; status: string; qa: { pass: boolean; decision: string } | null }[];
      };
    }>(`/briefs/idea-tree?product_id=${productId}`),
  getWeeklyDigest: (productId: string) =>
    http<{
      ok: boolean;
      winners_count: number;
      losers_count: number;
      qa_pending_count: number;
      winners: { code: string; ad_name: string | null }[];
      suggestion: string;
    }>(`/performance/weekly-digest?product_id=${productId}`),
  getActivity: (productId: string) =>
    http<{
      ok: boolean;
      activity: { id: string; kind: string; status: string; created_at: string; finished_at: string | null }[];
    }>(`/performance/activity?product_id=${productId}`),
  getPortfolio: (productId: string) =>
    http<{
      ok: boolean;
      portfolio: {
        variable: string;
        total_creatives: number;
        distinct_values: number;
        concentration: number;
        top_values: { value: string; count: number; share: number }[];
        flagged: boolean;
      }[];
    }>(`/performance/portfolio?product_id=${productId}`),
  getQaResults: (productId: string, onlyFailed?: boolean) =>
    http<{
      ok: boolean;
      results: { id: number; code: string; kind: string; pass: boolean; score: number; issues: string[]; summary: string; checked_at: string }[];
      fail_count: number;
      total_count: number;
    }>(`/performance/qa?product_id=${productId}${onlyFailed ? "&status=fail" : ""}`),
};
